import type { CalendarColourMode, GlowIntensity } from '@mfw/common';

import { CALENDAR_COLOUR_MODES, GLOW_INTENSITIES } from '@mfw/common';

import { getTenantOverride } from '../api/tenantOverride';
import config from '../config';

/**
 * THE LAST BRANDING THIS DEVICE SHOWED, so the next cold load can open in it.
 *
 * Without it every load painted three times — the catalog defaults (MyFitWorld
 * green and logo) first, the tenant theme once `byDomain` answered, and the
 * organization's rebrand only after the session, `/auth/me` and the switcher
 * had all answered. On a phone that last gap is long enough to see.
 *
 * What is kept is the RESOLVED branding (defaults ← tenant ← org, as
 * `AppThemeProvider` painted it) plus the logo, never the raw layers: the boot
 * paint in `main.tsx` runs before any provider exists and must not have to
 * merge anything. The live data still decides — once it has settled, it is
 * painted and written back here, so a changed or removed rebrand corrects
 * itself within one load.
 *
 * One entry per tenant (the super-admin override included, so switching tenant
 * cannot open in the previous one's colours); cleared on sign-out, so the next
 * account on a shared device does not open in someone else's organization.
 * Storage that throws or holds garbage reads as "nothing cached".
 */
export type CachedBranding = {
  theme: Record<string, string>;
  glow: GlowIntensity;
  calendarColours: CalendarColourMode;
  logoUrl: string | null;
};

const KEY_PREFIX = 'mfw.brandingCache.v1:';

const storageKey = () =>
  `${KEY_PREFIX}${getTenantOverride() || config.cloud.defaults.tenantDomain}`;

const isStringRecord = (value: unknown): value is Record<string, string> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every((entry) => typeof entry === 'string');

const parse = (raw: string | null): CachedBranding | null => {
  if (!raw) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null) return null;
  const { calendarColours, glow, logoUrl, theme } = value as Record<
    string,
    unknown
  >;
  if (
    !isStringRecord(theme) ||
    !(GLOW_INTENSITIES as readonly unknown[]).includes(glow) ||
    !(CALENDAR_COLOUR_MODES as readonly unknown[]).includes(calendarColours) ||
    (logoUrl !== null && typeof logoUrl !== 'string')
  ) {
    return null;
  }
  return {
    calendarColours: calendarColours as CalendarColourMode,
    glow: glow as GlowIntensity,
    logoUrl,
    theme,
  };
};

export function readBrandingCache() {
  try {
    return parse(window.localStorage.getItem(storageKey()));
  } catch {
    return null;
  }
}

export function writeBrandingCache(branding: CachedBranding) {
  try {
    window.localStorage.setItem(storageKey(), JSON.stringify(branding));
  } catch {
    // storage full or unavailable — the next load simply starts unbranded
  }
}

/** Every tenant's entry: a sign-out ends the account they were written for. */
export function clearBrandingCache() {
  try {
    const keys = Object.keys(window.localStorage).filter((key) =>
      key.startsWith(KEY_PREFIX),
    );
    for (const key of keys) window.localStorage.removeItem(key);
  } catch {
    // storage unavailable — there is nothing to clear
  }
}
